import {
  Dispatch,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";

import { AppContext } from "context/AppContext";
import { User, UserResult } from "services/types/ProfileTypes";
import { useSearchUser } from 'services/UserService';
import { QueryAction, QueryOption, SearchAction, SearchSort } from "./SearchReducer";
import UserList from "./UserList";

interface Props {
  queryOption: QueryOption;
  dispatchQuery: Dispatch<QueryAction>;
  setDataCount: (count: number | undefined) => void;
  closeHandler?: () => void;
  followHandler?: (user: User) => Promise<void>;
}

const SearchFilterUser: React.ForwardRefRenderFunction<any, Props> = ({
  queryOption,
  dispatchQuery,
  setDataCount,
  closeHandler,
  followHandler,
}, ref) => {
  const [users, setUsers] = useState<User[]>([]);

  // Get authKey from AppContext
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;

  useImperativeHandle(ref, () => {
    return {
      onKeywordChanged(keyword: string) {
        if (keyword !== queryOption.keyword) {
          setUsers([]);
        }
      },
      onSortChanged(sort?: SearchSort) {
        if (sort !== queryOption.sort) {
          setUsers([]);
        }
      }
    };
  }, [queryOption]);

  const { data, isLoading } = useSearchUser({
    keyword: queryOption.keyword || "",
    limit: queryOption.limit,
    page: queryOption.page || 1,
    token: authKey || ""
  });

  const { count, hasMore } = useMemo(() => {
    if (!data) {
      return { count: undefined, hasMore: false };
    }
    const result = (data as UserResult);
    setUsers(prevUsers => [...prevUsers, ...result.results]);
    return {
      count: result.meta.pagination.count,
      hasMore: result.meta.pagination.page < result.meta.pagination.pages,
    };
  }, [data]);

  const nextPageHandler = useCallback(() => {
    if (hasMore) {
      dispatchQuery({ type: SearchAction.ChangedPage });
    }
  }, [hasMore, dispatchQuery]);

  useEffect(() => setDataCount(count), [setDataCount, count]);

  return (
    <>
      {(isLoading || !!count) &&
        <UserList
          isLoading={isLoading}
          users={users}
          count={count}
          closeHandler={closeHandler}
          nextPageHandler={nextPageHandler}
          followHandler={followHandler} />}
    </>
  )
}

export default forwardRef(SearchFilterUser);